import type { Feed } from "@/store/types";

/** The ids at a thread's two ends, and the feed they belong to, as one render showed them. */
export type ThreadEnds = { feedId: string; oldest: string; newest: string };

/**
 * What changed at a thread's ends between two renders:
 * - `opened` — the first render with entries, or a different feed.
 * - `prepended` — a new oldest entry, so something landed above; a new newest entry may have landed too.
 * - `appended` — only a new newest entry, so something landed at the foot.
 * - `none` — the same ends; content may still have filled in between them.
 */
export type ThreadEndChange = "opened" | "prepended" | "appended" | "none";

/**
 * Compares a thread's ends with the last render's, by id rather than by count.
 *
 * @param before - The last render's ends; `null` before the thread showed any entry.
 * @param after - This render's ends; `null` when it shows none.
 * @returns Which end changed.
 */
export function threadEndChange(before: ThreadEnds | null, after: ThreadEnds | null): ThreadEndChange {
  if (!before || !after || before.feedId !== after.feedId) return "opened";
  if (after.oldest !== before.oldest) return "prepended";
  if (after.newest !== before.newest) return "appended";
  return "none";
}

/**
 * The floor to request the next older page from, deduped the way the feed list dedupes: one request per floor.
 *
 * @param feed - The feed's row, or `undefined` when the store has none yet.
 * @param requestedFloor - The floor the last page was requested from; `null` when none is pending a retry.
 * @returns The floor to page from, `"tail"` for a feed with no contiguous span yet, or `null` when there is nothing to
 *   request: the feed holds the start of its history, or this floor was already requested.
 */
export function olderPageFloor(
  feed: Pick<Feed, "loadedFirstPage" | "oldestContiguousCreatedAt"> | undefined,
  requestedFloor: string | null,
): string | null {
  if (feed?.loadedFirstPage === true) return null;
  const floor = feed?.oldestContiguousCreatedAt ?? "tail";
  return floor === requestedFloor ? null : floor;
}
